import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * In-app confirm dialog — drop-in replacement for `window.confirm()`.
 *
 * Wrap the app once at the root:
 *   <ConfirmProvider> … </ConfirmProvider>
 *
 * Use imperatively from any component:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Delete this thing?',
 *     description: 'Cannot be undone.',
 *     confirmLabel: 'Delete',
 *     destructive: true,
 *   });
 *   if (!ok) return;
 *
 * Returns Promise<boolean>. Resolves false on Cancel / ESC / backdrop click.
 *
 * Visual styling matches the tightened modal idiom used elsewhere
 * (rounded-lg, solid buttons, no gradients, no hover:scale). Renders
 * via a portal to document.body so it escapes any stacking context
 * — confirms fired from inside other modals still land on top.
 */

const ConfirmContext = createContext(null);

/**
 * The hook. Returns an async `confirm(options)` function.
 */
export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Safety net — if someone forgets to wrap <ConfirmProvider>, fall back
    // to the browser confirm so the app still works (just looks cheap).
    // Emit a warning so it's visible in dev.
    // eslint-disable-next-line no-console
    console.warn('useConfirm: no ConfirmProvider found, falling back to window.confirm');
    return async (options) => window.confirm(options?.title || 'Are you sure?');
  }
  return ctx;
};

export const ConfirmProvider = ({ children }) => {
  // `state` is null when no dialog is open, otherwise holds the resolve
  // function + options passed by the caller.
  const [state, setState] = useState(null);
  const previouslyFocusedRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      previouslyFocusedRef.current = document.activeElement;
      setState({
        options: {
          title: options.title || 'Are you sure?',
          description: options.description || null,
          confirmLabel: options.confirmLabel || 'Confirm',
          cancelLabel: options.cancelLabel || 'Cancel',
          destructive: Boolean(options.destructive),
        },
        resolve,
      });
    });
  }, []);

  const close = useCallback((result) => {
    if (!state) return;
    state.resolve(result);
    setState(null);
    // Restore focus to whatever triggered the dialog.
    requestAnimationFrame(() => {
      if (previouslyFocusedRef.current && typeof previouslyFocusedRef.current.focus === 'function') {
        previouslyFocusedRef.current.focus();
      }
    });
  }, [state]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && <ConfirmDialog state={state} onClose={close} />}
    </ConfirmContext.Provider>
  );
};

/**
 * Internal dialog — portal, focus trap, ESC handling, backdrop click.
 * Not exported; only the provider instantiates it.
 */
const ConfirmDialog = ({ state, onClose }) => {
  const { options } = state;
  const dialogRef = useRef(null);
  const cancelBtnRef = useRef(null);
  const confirmBtnRef = useRef(null);

  const titleId = 'confirm-dialog-title';
  const descriptionId = 'confirm-dialog-description';

  // Focus the Cancel button on mount (safer default for destructive actions).
  useEffect(() => {
    const t = requestAnimationFrame(() => {
      if (cancelBtnRef.current) cancelBtnRef.current.focus();
    });
    return () => cancelAnimationFrame(t);
  }, []);

  // Global key handling: ESC cancels, Tab cycles between the two buttons
  // (minimal focus trap — the dialog only has two focusables so a full
  // trap library would be overkill).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose(false);
      } else if (e.key === 'Tab') {
        // Two-element focus trap. Cancel <-> Confirm only.
        const cancel = cancelBtnRef.current;
        const confirmBtn = confirmBtnRef.current;
        if (!cancel || !confirmBtn) return;
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === cancel) {
            e.preventDefault();
            confirmBtn.focus();
          }
        } else {
          if (active === confirmBtn) {
            e.preventDefault();
            cancel.focus();
          }
        }
      } else if (e.key === 'Enter') {
        // Enter on the confirm button fires confirm; other focuses pass through.
        if (document.activeElement === confirmBtnRef.current) {
          e.preventDefault();
          onClose(true);
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const confirmButtonClass = options.destructive
    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
    : 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500';

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm"
      onClick={() => onClose(false)}
      aria-hidden={false}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={options.description ? descriptionId : undefined}
        className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={titleId} className="text-base font-semibold text-gray-900 dark:text-white mb-1">
          {options.title}
        </h3>
        {options.description && (
          <p id={descriptionId} className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {options.description}
          </p>
        )}
        {!options.description && <div className="mb-4" />}
        <div className="flex gap-2 justify-end">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={() => onClose(false)}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-gray-400"
          >
            {options.cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={() => onClose(true)}
            className={`px-3 py-1.5 text-sm font-medium text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${confirmButtonClass}`}
          >
            {options.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmProvider;
